/**
 * Skeleton loaders.
 *
 * INTERACTION_SPEC §quality bar:
 *   - Skeletons for lists/tables/cards >150ms
 *   - Spinners ONLY for full-page loads on cold start
 *   - Inline progress (button states) for actions
 *
 * Shimmer per §2: 1.2s linear gradient sweep, rgba(255,255,255,0.04)
 * over bg-ink-700. The `.shimmer` class + @keyframes live in
 * globals.css; reduced-motion users get a static skeleton via the
 * global media-query at the bottom of that file.
 *
 * We deliberately stick to additive utility classes here so consumers
 * can pass arbitrary sizing (h-, w-, rounded-).
 */

import type { ReactNode } from 'react';
import { cn } from '@/lib/cn.js';

interface SkeletonProps {
  className?: string;
  /** Render as inline-block (default) vs block. */
  inline?: boolean;
}

export function Skeleton({ className, inline }: SkeletonProps) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        inline ? 'inline-block' : 'block',
        'bg-ink-700 rounded shimmer',
        className
      )}
    />
  );
}

/**
 * SkeletonList — N rows of equal height. Used by inbox, history,
 * queue lists. Matches design handoff §2 — "12 rows, grid matches
 * layout, shimmer left-to-right, 1.2s loop". Pulse here is tailwind's
 * default; the proper shimmer keyframe lives in globals.css and can
 * be opted into via `shimmer` className when the row layout is more
 * complex than a flat box.
 */
interface SkeletonListProps {
  count?: number;
  rowHeight?: number;
  className?: string;
  /** Per-row content. Defaults to a flat shimmering bar; pass children
   *  for a structured row (e.g. icon + two text lines). */
  children?: ReactNode;
}

export function SkeletonList({
  count = 8,
  rowHeight = 56,
  className,
  children,
}: SkeletonListProps) {
  return (
    <ul className={cn('divide-y divide-ink-600/40', className)} aria-busy="true">
      {Array.from({ length: count }).map((_, i) => (
        <li key={i} style={{ height: rowHeight }} className="px-3 flex items-center">
          {children ?? (
            <Skeleton className="h-3 w-2/3 max-w-[420px]" />
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * SkeletonText — N lines stacked. Use inside cards / panels for
 * paragraph-shaped placeholders.
 */
interface SkeletonTextProps {
  lines?: number;
  className?: string;
}

export function SkeletonText({ lines = 3, className }: SkeletonTextProps) {
  return (
    <div className={cn('space-y-2', className)} aria-busy="true">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={cn('h-3', i === lines - 1 ? 'w-2/3' : 'w-full')}
        />
      ))}
    </div>
  );
}
